import { cn } from "cn";
import {
  type ComponentProps,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { Button } from "@/components/ui/button/button";
import { useAudioRecorder } from "@/features/shared/audio-recorder/_hooks/use-audio-recorder";

type AudioRecorderContextValue = ReturnType<typeof useAudioRecorder> & {
  onRecordEnd?: (data: { blob: Blob; url: string }) => void;
  onCancel?: (data?: { blob: Blob | null; url: string | null }) => void;
};

const AudioRecorderContext = createContext<AudioRecorderContextValue | null>(
  null,
);

function useAudioRecorderContext() {
  const context = useContext(AudioRecorderContext);
  if (!context) {
    throw new Error("AudioRecorder.* must be used within <AudioRecorder>");
  }
  return context;
}

type AudioRecorderProps = ComponentProps<"div"> & {
  onRecordEnd?: (data: { blob: Blob; url: string }) => void;
  onCancel?: (data?: { blob: Blob | null; url: string | null }) => void;
  /** Fired whenever the mic opens or closes — lets the host refuse to close over a live clip. */
  onRecordingChange?: (isRecording: boolean) => void;
  autoStart?: boolean;
  /** Increment this number to request stopping the current recording */
  stopSignal?: boolean;
};

function AudioRecorder({
  className,
  children,
  onRecordEnd,
  onCancel,
  onRecordingChange,
  autoStart,
  stopSignal,
  ...props
}: AudioRecorderProps) {
  const recorder = useAudioRecorder();

  useEffect(() => {
    if (!autoStart) return;
    const id = setTimeout(() => {
      recorder.startRecording();
    }, 0);
    return () => clearTimeout(id);
  }, [autoStart, recorder.startRecording]);

  useEffect(() => {
    if (!stopSignal) return;
    if (recorder.isRecording) {
      recorder.stopRecording();
    }
  }, [stopSignal, recorder.isRecording, recorder.stopRecording]);

  useEffect(() => {
    onRecordingChange?.(recorder.isRecording);
  }, [recorder.isRecording, onRecordingChange]);

  return (
    <AudioRecorderContext.Provider
      value={{ ...recorder, onRecordEnd, onCancel }}
    >
      <div data-slot="audio-recorder" className={cn(className)} {...props}>
        {children}
      </div>
    </AudioRecorderContext.Provider>
  );
}

type AudioRecorderWaveProps = ComponentProps<"div">;

function AudioRecorderWave({ className, ...props }: AudioRecorderWaveProps) {
  const { containerRef, audioUrl, audioBlob, onRecordEnd } =
    useAudioRecorderContext();
  useEffect(() => {
    if (audioUrl) {
      onRecordEnd?.({ blob: audioBlob ?? new Blob(), url: audioUrl });
    }
  }, [audioUrl, audioBlob, onRecordEnd]);
  return (
    <div
      data-slot="audio-recorder-wave"
      ref={containerRef}
      className={cn("waveform w-full relative overflow-hidden", className)}
      {...props}
    />
  );
}

type AudioRecorderDurationProps = ComponentProps<"span"> & {
  format?: "mm:ss" | "ss";
};

function formatSecondsToClock(totalSeconds: number, mode: "mm:ss" | "ss") {
  const clamped = Math.max(0, Math.floor(totalSeconds ?? 0));
  if (mode === "ss") return String(clamped);
  const minutes = Math.floor(clamped / 60);
  const seconds = clamped % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function AudioRecorderDuration({
  className,
  format = "mm:ss",
  ...props
}: AudioRecorderDurationProps) {
  const { duration } = useAudioRecorderContext();
  return (
    <span
      data-slot="audio-recorder-duration"
      className={cn(
        /* sorted */
        "shrink-0",
        "w-12",
        "text-xs text-text-dark-low text-right",
        className,
      )}
      {...props}
    >
      {formatSecondsToClock(duration, format)}
    </span>
  );
}

type AudioRecorderTogglePlayProps = ComponentProps<typeof Button>;

function AudioRecorderTogglePlay({
  className,
  children,
  ...props
}: AudioRecorderTogglePlayProps) {
  const { isPaused, toggleRecordingPause } = useAudioRecorderContext();

  const handleClick = useCallback(() => {
    toggleRecordingPause();
  }, [toggleRecordingPause]);

  const computedLabel = useMemo(() => {
    return isPaused ? "Resume" : "Pause";
  }, [isPaused]);

  return (
    <Button
      data-slot="audio-recorder-toggle-play"
      data-state={isPaused ? "paused" : "recording"}
      onClick={handleClick}
      className={cn(className)}
      {...props}
    >
      {children ?? computedLabel}
    </Button>
  );
}

type AudioRecorderSaveProps = ComponentProps<typeof Button>;

function AudioRecorderSave({
  className,
  children = "Save",
  asChild,
  ...props
}: AudioRecorderSaveProps) {
  const { stopRecording } = useAudioRecorderContext();
  const handleSave = useCallback(() => {
    stopRecording();
  }, [stopRecording]);

  return (
    <Button
      data-slot="audio-recorder-save"
      onClick={handleSave}
      className={cn(className)}
      {...props}
    >
      {children}
    </Button>
  );
}

type AudioRecorderCancelProps = ComponentProps<typeof Button> & {
  onCancel?: () => void;
};

function AudioRecorderCancel({
  className,
  children = "Cancel",
  onCancel: onCancelProp,
  ...props
}: AudioRecorderCancelProps) {
  const {
    isRecording,
    stopRecording,
    clearRecording,
    audioBlob,
    audioUrl,
    onCancel,
  } = useAudioRecorderContext();

  const handleCancel = useCallback(() => {
    // If currently recording, stop then clear shortly after
    if (isRecording) {
      stopRecording();
      setTimeout(() => {
        clearRecording();
        onCancel?.();
        onCancelProp?.();
      }, 0);
      return;
    }
    const data = { blob: audioBlob, url: audioUrl };
    clearRecording();
    onCancel?.(data);
    onCancelProp?.();
  }, [
    isRecording,
    stopRecording,
    clearRecording,
    audioBlob,
    audioUrl,
    onCancel,
    onCancelProp,
  ]);

  return (
    <Button
      data-slot="audio-recorder-cancel"
      onClick={handleCancel}
      className={cn(className)}
      variant="neutral"
      kind="soft"
      {...props}
    >
      {children}
    </Button>
  );
}

export {
  AudioRecorder,
  AudioRecorderCancel,
  AudioRecorderDuration,
  AudioRecorderSave,
  AudioRecorderTogglePlay,
  AudioRecorderWave,
};
